import React, {useCallback, useMemo} from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {ACTION_REFRESH} from 'notebook/store/VersioningConstants';
import {
  ActionFunctionContext,
  useActionFunctionContext,
} from 'notebook/structured-tables/action-functions/ActionFunctionContext';
import makeCustomButtonActionFunction from 'notebook/structured-tables/action-functions/makeCustomButtonActionFunction';
import {
  ActionResultType,
  performClientSideWriteAction,
  performServerSideWriteAction,
} from 'notebook/structured-tables/action-functions/PerformActionFunctions';
import {ResyncOptionsModal} from 'notebook/structured-tables/action-functions/ResyncOptionsModal';
import unsupportedOverflowMenuItem from 'notebook/structured-tables/action-functions/unsupportedOverflowMenuItem';
import * as ActionFunctionDAO from 'notebook/structured-tables/ActionFunctionDAO';
import {
  StructuredTable,
  StructuredTableKind,
  TableActionFunctionParameters,
} from 'notebook/structured-tables/StructuredTableConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {REFRESH_LOOKUP_COLUMN_OPTION, RESYNC_TABLE_LABEL} from 'notebook/tables/constants/TableConstants';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {handleRefreshLookupStructuredTable} from 'notebook/tables/refresh-table-data/RefreshLookupColumnData';
import {
  handleRefreshEntitiesInStructuredTable,
  handleRefreshStructuredTable,
} from 'notebook/tables/refresh-table-data/RefreshTableData';
import TableUtil from 'notebook/tables/TableUtil';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Modal from 'ui-library/modals/Modal';

import rowHasUnsubmittedChanges from '../util/rowHasUnsubmittedChanges';

export const FORMULA_PRESENT_WARNING = 'Formulas in submitted rows will be overwritten';
export const UNSUBMITTED_CHANGES_IN_SUBMITTED_ROWS_WARNING = 'Changes to submitted rows will be lost';
export const EDITS_IN_UNSUBMITTED_ROWS_WARNING = 'Edits in unsubmitted rows may be lost';

export function RefreshWarningModal({
  warnings,
  onRequestClose,
  onRequestSubmit,
}: {
  warnings: Array<string>;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}): JSX.Element {
  const warningContent = warnings.map((warning) => <li key={warning}>{warning}</li>);

  return (
    <Modal
      title="Are you sure you'd like to continue?"
      onRequestClose={onRequestClose}
      onRequestSubmit={onRequestSubmit}
      buttons={[
        {text: 'Yes, continue', submit: true},
        {text: 'Cancel', cancel: true},
      ]}
    >
      <InlineBanner
        bannerType={InlineBannerType.WARNING}
        text="Re-syncing this table will modify data:"
        supportingInfo={<ul>{warningContent}</ul>}
      />
    </Modal>
  );
}

function onInvokeRefreshAllAction(
  props: Pick<
    TableActionFunctionParameters<{}, {}>,
    'entryOrTemplateId' | 'structuredTableAdapter' | 'structuredTable' | 'computedTable' | 'updateHandler'
  >
): void {
  const {entryOrTemplateId, structuredTableAdapter, structuredTable, computedTable, updateHandler} = props;
  assert(entryOrTemplateId != null, 'RefreshTable action cannot be invoked outside a notebook entry');

  const doRefresh = async (): Promise<void> => {
    await handleRefreshStructuredTable({
      entryOrTemplateId,
      structuredTableAdapter,
      structuredTable,
      computedTable,
      onUpdateTable: updateHandler,
    });
  };

  performClientSideWriteAction({
    entryOrTemplateId,
    loadingScreenMessage: 'Re-syncing...',
    autoSaverAction: ACTION_REFRESH,
    doPerformAction: doRefresh,
    analyticsEvent: ELN_ANALYTICS_ACTIONS.REFRESHED_STRUCTURED_TABLE,
    analyticsMetadata: {
      structuredTableKind: structuredTable.kind,
      structuredTableApiIdentifier: structuredTable.apiId ?? null,
      fileId: entryOrTemplateId,
    },
  });
}

/**
 * Server-side re-sync: invokes the backend `resync` action function instead of running the row-updater
 * pipeline in the client. Gated behind ELN_RESYNC_USE_BACKEND_ACTION_FUNCTION.
 */
function onInvokeBackendResyncAction(
  props: Pick<
    TableActionFunctionParameters<{}, {}>,
    'entryOrTemplateId' | 'structuredTable' | 'computedTable'
  > & {actionFunctionContext: ActionFunctionContext}
): void {
  const {entryOrTemplateId, structuredTable, computedTable, actionFunctionContext} = props;
  assert(entryOrTemplateId != null, 'Resync action cannot be invoked outside a notebook entry');

  performServerSideWriteAction<ActionFunctionDAO.ResyncResponse>({
    entryId: entryOrTemplateId,
    loadingScreenMessage: 'Re-syncing...',
    autoSaverAction: ACTION_REFRESH,
    doPerformAction: async () => {
      const responseData = await ActionFunctionDAO.resync(
        entryOrTemplateId,
        structuredTable,
        computedTable,
        actionFunctionContext
      );
      return {resultType: ActionResultType.SUCCESS, responseData};
    },
  });
}

function onInvokeRefreshEntitiesAction(
  props: Pick<
    TableActionFunctionParameters<{}, {}>,
    'entryOrTemplateId' | 'structuredTableAdapter' | 'structuredTable' | 'computedTable' | 'updateHandler'
  >
): void {
  const {entryOrTemplateId, structuredTableAdapter, structuredTable, computedTable, updateHandler} = props;
  assert(entryOrTemplateId != null, 'RefreshEntities action cannot be invoked outside a notebook entry');

  const doRefresh = async (): Promise<void> => {
    await handleRefreshEntitiesInStructuredTable({
      entryOrTemplateId,
      structuredTableAdapter,
      structuredTable,
      computedTable,
      onUpdateTable: updateHandler,
    });
  };

  performClientSideWriteAction({
    entryOrTemplateId,
    loadingScreenMessage: 'Refreshing table...',
    autoSaverAction: ACTION_REFRESH,
    doPerformAction: doRefresh,
    analyticsEvent: ELN_ANALYTICS_ACTIONS.REFRESHED_STRUCTURED_TABLE_ENTITIES_ONLY,
    analyticsMetadata: {
      structuredTableKind: structuredTable.kind,
      structuredTableApiIdentifier: structuredTable.apiId ?? null,
      fileId: entryOrTemplateId,
    },
  });
}

function onInvokeRefreshLookupColumnsAction(
  props: Pick<TableActionFunctionParameters<{}, {}>, 'entryOrTemplateId' | 'structuredTable'>
): void {
  const {entryOrTemplateId, structuredTable} = props;
  assert(
    entryOrTemplateId != null,
    'RefreshTable action for lookup columns cannot be invoked outside a notebook entry'
  );

  const doRefresh = (): Promise<void> => {
    handleRefreshLookupStructuredTable({
      entryOrTemplateId,
      structuredTable,
    });
    return Promise.resolve();
  };
  performClientSideWriteAction({
    entryOrTemplateId,
    loadingScreenMessage: 'Looking up new data...',
    autoSaverAction: ACTION_REFRESH,
    doPerformAction: doRefresh,
    analyticsEvent: ELN_ANALYTICS_ACTIONS.REFRESHED_STRUCTURED_TABLE_LOOKUP_COLUMNS,
    analyticsMetadata: {
      structuredTableKind: structuredTable.kind,
      structuredTableApiIdentifier: structuredTable.apiId ?? null,
      fileId: entryOrTemplateId,
    },
  });
}

export function addTableRefreshWarnings(
  structuredTable: StructuredTable<unknown, unknown>,
  computedTable: Array<Array<EvaluatedCell>>
): [
  unsubmittedChangesInSubmittedRows: boolean,
  possibleEditsInUnsubmittedRows: boolean,
  formulaPresent: boolean,
] {
  let unsubmittedChangesInSubmittedRow = false;
  let possibleEditsInUnsubmittedRows = false;
  let formulaPresent = false;
  for (let rowIndex = 0; rowIndex < structuredTable.rows.length; rowIndex++) {
    if (structuredTable.rows[rowIndex].isFinalized) {
      if (
        rowHasUnsubmittedChanges(
          computedTable[rowIndex],
          structuredTable.rows[rowIndex],
          structuredTable.columns,
          structuredTable.schemas
        )
      ) {
        unsubmittedChangesInSubmittedRow = true;
      }
      for (let columnIndex = 0; columnIndex < structuredTable.rows[rowIndex].cells.length; columnIndex++) {
        if (TableUtil.isCellFormula(structuredTable.rows[rowIndex].cells[columnIndex])) {
          formulaPresent = true;
        }
      }
    } else if (
      [StructuredTableKind.INVENTORY, StructuredTableKind.MIXTURE_INGREDIENTS].includes(structuredTable.kind)
    ) {
      // Inventory tables may have unsubmitted rows with cells that are both editable and auto-populated,
      // so we show a warning that edits may be overwritten by re-sync.
      // Mixture Prep Ingredients tables have all unsubmitted rows deleted during re-sync.
      possibleEditsInUnsubmittedRows = true;
    }
    if (unsubmittedChangesInSubmittedRow && formulaPresent) {
      break;
    }
  }
  return [unsubmittedChangesInSubmittedRow, possibleEditsInUnsubmittedRows, formulaPresent];
}

function RefreshTableButton({params}: {params: TableActionFunctionParameters<{}, {}>}): JSX.Element {
  const {entryOrTemplateId, structuredTableAdapter, structuredTable, computedTable, updateHandler, locked} =
    params;

  const useNewResyncModal = BenchlingConfig.ELN_ENABLE_STRUCTURED_TABLE_NEW_RESYNC_MODAL;
  const useBackendResync = BenchlingConfig.ELN_RESYNC_USE_BACKEND_ACTION_FUNCTION;
  const actionFunctionContext = useActionFunctionContext();

  const [unsubmittedChangesInSubmittedRow, possibleEditsInUnsubmittedRows, formulaPresent] = useMemo(
    () => addTableRefreshWarnings(structuredTable, computedTable),
    [computedTable, structuredTable]
  );

  // Only show the ResyncOptionsModal when there are formulas or submitted-row changes that the
  // user needs to decide how to handle. Pure "possible edits in unsubmitted rows" does not require
  // modal interaction — the entities-only refresh handles that case gracefully.
  const shouldShowResyncModal = formulaPresent || unsubmittedChangesInSubmittedRow;

  const refreshAllAction = useCallback(() => {
    if (useBackendResync) {
      onInvokeBackendResyncAction({
        entryOrTemplateId,
        structuredTable,
        computedTable,
        actionFunctionContext,
      });
      return;
    }
    if (useNewResyncModal) {
      if (shouldShowResyncModal) {
        legacyRunModal(ResyncOptionsModal, {
          unsubmittedChangesInSubmittedRow,
          possibleEditsInUnsubmittedRows,
          formulaPresent,
          onRequestKeepFormulas: () => {
            onInvokeRefreshEntitiesAction({
              entryOrTemplateId,
              structuredTableAdapter,
              structuredTable,
              computedTable,
              updateHandler,
            });
          },
          onRequestRemoveFormulas: () => {
            onInvokeRefreshAllAction({
              entryOrTemplateId,
              structuredTableAdapter,
              structuredTable,
              computedTable,
              updateHandler,
            });
          },
        });
      } else {
        onInvokeRefreshEntitiesAction({
          entryOrTemplateId,
          structuredTableAdapter,
          structuredTable,
          computedTable,
          updateHandler,
        });
      }
    } else {
      const legacyWarnings: Array<string> = [];
      if (unsubmittedChangesInSubmittedRow) {
        legacyWarnings.push(UNSUBMITTED_CHANGES_IN_SUBMITTED_ROWS_WARNING);
      }
      if (possibleEditsInUnsubmittedRows) {
        legacyWarnings.push(EDITS_IN_UNSUBMITTED_ROWS_WARNING);
      }
      if (formulaPresent) {
        legacyWarnings.push(FORMULA_PRESENT_WARNING);
      }
      if (legacyWarnings.length) {
        legacyRunModal(RefreshWarningModal, {
          warnings: legacyWarnings,
          onRequestSubmit: () => {
            onInvokeRefreshAllAction({
              entryOrTemplateId,
              structuredTableAdapter,
              structuredTable,
              computedTable,
              updateHandler,
            });
          },
        });
      } else {
        onInvokeRefreshAllAction({
          entryOrTemplateId,
          structuredTableAdapter,
          structuredTable,
          computedTable,
          updateHandler,
        });
      }
    }
  }, [
    useBackendResync,
    actionFunctionContext,
    useNewResyncModal,
    shouldShowResyncModal,
    entryOrTemplateId,
    structuredTableAdapter,
    structuredTable,
    computedTable,
    updateHandler,
    formulaPresent,
    unsubmittedChangesInSubmittedRow,
    possibleEditsInUnsubmittedRows,
  ]);

  const refreshLookupColumnsAction = useCallback(() => {
    onInvokeRefreshLookupColumnsAction({entryOrTemplateId, structuredTable});
  }, [entryOrTemplateId, structuredTable]);

  const isRefreshLookupDisabled = useMemo(
    () => locked || !structuredTable.columns.some((c) => c.columnType === TableColumnType.DEDICATED_LOOKUP),
    [locked, structuredTable.columns]
  );

  return (
    <Tooltip title={RESYNC_TABLE_LABEL}>
      <Dropdown
        menu={
          <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.MEDIUM}>
            <Option
              key={`refresh-lookup-columns-${structuredTable.provenanceUuid}`}
              isDisabled={isRefreshLookupDisabled}
              onSelect={refreshLookupColumnsAction}
              shouldEllipsize={true}
            >
              {REFRESH_LOOKUP_COLUMN_OPTION}
            </Option>
            <Option
              key={`refresh-all-${structuredTable.provenanceUuid}`}
              isDisabled={locked}
              onSelect={refreshAllAction}
              shouldEllipsize={true}
            >
              {RESYNC_TABLE_LABEL}
            </Option>
          </Menu>
        }
      >
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.MINI}
          className="u-mini-spacing-left"
          disabled={locked}
          icon="arrows-rotate"
          iconAltText={RESYNC_TABLE_LABEL}
          isDropdown={true}
          dataTestAttrs={{
            component: 'RefreshTableButton',
            element: 'button',
          }}
        />
      </Dropdown>
    </Tooltip>
  );
}

export function renderDropdownButton<TTableMetadata, TTableRowMetadata>(
  props: TableActionFunctionParameters<TTableMetadata, TTableRowMetadata>
): JSX.Element {
  return <RefreshTableButton params={props as TableActionFunctionParameters<{}, {}>} />;
}

/**
 * An ActionFunction that refreshes the contents of a table.
 *
 * If the entry the table is contained in is locked, then the button
 * to refresh is disabled.
 */

export const refreshTableActionFunction = makeCustomButtonActionFunction({
  renderToolbarButton: renderDropdownButton,
  renderOverflowMenuItem: unsupportedOverflowMenuItem,
});
